'use client';
import { useState } from 'react';
import { useT } from '../../lib/i18n';
import { isEmail, uidError, validateSignup, type Errors, type SignupInput } from '../../lib/validate';
import { AuthCodeField, FieldMsg, Label, PwInput, SendCodeButton, SocialButtons, useCountdown, ZipField, useInputFilter } from '../../components/FormBits';
import { NoticeLink } from '../../components/NoticeLayer';
import { Icon } from '../../components/Icon';
import { SmartLink } from '../../components/SmartLink';
import { Working } from '../../components/Working';

/**
 * 회원가입 3단계 — 가이드 register.html 의 스텝 구조·클래스를 그대로 쓴다.
 *
 * 인증 수단은 **마켓 설정이 정한다**(사용자 참고 3) — 둘 다 켜져 있으면 회원이 고르고,
 * 하나만 켜져 있으면 그 칸만 나오며 선택지를 보여 주지 않는다(고를 것이 없으므로).
 * 서버는 둘 중 하나라도 켜져 있으면 인증코드를 요구한다.
 *
 * 유효성은 **현행 규칙**(lib/validate — register2.php 계승)이다.
 */
type Step = 1 | 2 | 3;
type AuthType = 'email' | 'phone';

export function JoinFlow({ authEmail, authPhone, autoAddress, joinPoint, social }: {
  authEmail: boolean;
  authPhone: boolean;
  autoAddress: boolean;
  joinPoint: number;
  social: { google: boolean; apple: boolean; kakao: boolean; naver: boolean };
}) {
  const t = useT();
  const [step, setStep] = useState<Step>(1);

  // 1단계 — 약관
  const [agree, setAgree] = useState({ terms: false, privacy: false, marketing: false });
  const [agreeError, setAgreeError] = useState<string | null>(null);

  /** 약관 유효성 — 버튼을 잠그기만 하면 왜 안 되는지 모른다(2026-08-22 사용자 지적) */
  const nextFromAgree = () => {
    if (!agree.terms && !agree.privacy) { setAgreeError(t('acc.err.agreeBoth')); return; }
    if (!agree.terms) { setAgreeError(t('acc.err.agreeTerms')); return; }
    if (!agree.privacy) { setAgreeError(t('acc.err.agreePrivacy')); return; }
    setAgreeError(null);
    goStep(2);
  };

  // 2단계 — 입력
  const needsCode = authEmail || authPhone;
  const [authType, setAuthType] = useState<AuthType>(authEmail ? 'email' : 'phone');
  const [f, setF] = useState<SignupInput>({
    uid: '', password: '', passwordConfirm: '', name: '',
    email: '', mobile: '', birthday: '', zipcode: '', address: '', addressDetail: '',
  });
  const set = (p: Partial<SignupInput>) => setF((s) => ({ ...s, ...p }));
  /** 규칙 밖 글자를 걷어 내고 그 자리에서 까닭을 말한다(한글 입력이 조용히 사라지던 것, 2026-09-02) */
  const drop = useInputFilter();
  const [recvEmail, setRecvEmail] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  /*
   * 인증코드 발송 안내 — **그 칸 아래**에 뜬다(2026-09-02 사용자 지적 — 주소 아래에 떴다).
   * 아래쪽 `notice` 는 폼 전체에 대한 말(유효성 첫 오류·가입 실패)이라 자리가 다르다.
   * 이메일 인증이면 이메일 칸, 알림톡 인증이면 연락처 칸 아래다 — 방금 누른 버튼 옆이다.
   */
  const [codeNotice, setCodeNotice] = useState<{ text: string; error: boolean } | null>(null);

  // 아이디 중복확인 — 확인한 아이디를 기억해 두고, 아이디를 고치면 다시 확인시킨다
  const [checkedUid, setCheckedUid] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const { left, start } = useCountdown(180);

  const goStep = (n: Step) => { setStep(n); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  /**
   * 아이디 중복확인 — **형식부터 본다**(2026-08-22 사용자 지적: 한글을 넣어도 통과처럼 보였다).
   * 규칙에 맞지 않으면 서버에 물어볼 것도 없다(서버도 같은 규칙으로 거절한다).
   */
  const checkUid = async () => {
    setErrors((e) => ({ ...e, uid: undefined }));
    const uidErr = uidError(f.uid, t);
    if (uidErr) { setErrors((e) => ({ ...e, uid: uidErr })); setCheckedUid(null); return; }
    setBusy(true);
    try {
      const res = await fetch(`/api/members/check-id?uid=${encodeURIComponent(f.uid.trim())}`);
      const body = (await res.json().catch(() => ({}))) as { available?: boolean };
      // 통과 문구는 **아이디 칸 아래**에 이미 나온다(FieldMsg) — 폼 아래에 또 적지 않는다
      if (body.available) { setCheckedUid(f.uid.trim()); }
      else { setCheckedUid(null); setErrors((e) => ({ ...e, uid: t('acc.err.uidTaken') })); }
    } finally { setBusy(false); }
  };

  /**
   * 인증코드 발송 — 보내기 **전에** 대상 칸을 검사한다(2026-08-22 사용자 지적:
   * 이메일 칸에 "010" 을 넣고도 발송이 됐다). 코드 발급 키는 이메일이라
   * 알림톡 인증이어도 이메일 형식을 본다(휴대폰은 따로 검사).
   * 이미 가입된 이메일은 **서버가 발송 전에 막는다**(409) — 그 메시지를 그대로 보여 준다.
   */
  const sendCode = async () => {
    setNotice(null);
    setCodeNotice(null);
    const nextErr: Errors = {};
    if (!f.email.trim()) nextErr.email = t('acc.err.emailRequired');
    else if (!isEmail(f.email.trim())) nextErr.email = t('acc.err.emailFormat');
    if (authType === 'phone') {
      if (!f.mobile.trim()) nextErr.mobile = t('acc.err.mobileRequired');
      else if (f.mobile.length < 9) nextErr.mobile = t('acc.err.mobileFormat');
    }
    setErrors((e) => ({ ...e, ...nextErr, email: nextErr.email, mobile: nextErr.mobile }));
    if (nextErr.email || nextErr.mobile) return;

    setBusy(true);
    try {
      const res = await fetch('/api/members/auth-code', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: f.email.trim(), purpose: 'register' }),
      });
      const body = (await res.json().catch(() => ({}))) as { message?: string };
      if (!res.ok) {
        // 409 = 이미 가입된 이메일. 그 밖은 발송 실패
        setErrors((e) => ({ ...e, email: res.status === 409 ? (body.message ?? t('acc.err.emailTaken')) : undefined }));
        if (res.status !== 409) setCodeNotice({ text: body.message ?? t('acc.err.codeSendFailed'), error: true });
        return;
      }
      setCodeSent(true); start(); setCodeNotice({ text: t('acc.codeSent'), error: false });
    } finally { setBusy(false); }
  };

  const submit = async () => {
    setNotice(null);
    const e = validateSignup(f, t);
    if (checkedUid !== f.uid.trim()) e.uid = t('acc.err.uidCheckNeeded');
    if (needsCode && code.length !== 6) e.authCode = t('acc.err.codeRequired');
    setErrors(e);
    const first = Object.values(e).find((v) => !!v);
    if (first) { setNotice(first); return; }

    setBusy(true);
    try {
      const res = await fetch('/api/members', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          uid: f.uid.trim(), password: f.password, email: f.email.trim(), name: f.name.trim(),
          birthday: f.birthday, mobile: f.mobile,
          zipcode: f.zipcode, address: `${f.address} ${f.addressDetail}`.trim(),
          recvEmail, ...(needsCode ? { authCode: code } : {}),
        }),
      });
      if (res.ok) { goStep(3); return; }
      const body = (await res.json().catch(() => ({}))) as { message?: string };
      setNotice(body.message ?? t('acc.err.joinFailed'));
    } catch {
      setNotice(t('acc.err.network'));
    } finally { setBusy(false); }
  };

  return (
    <>
      {/* 스텝 표시 — 가이드 reg-steps */}
      <div className="reg-steps">
        {([1, 2, 3] as Step[]).map((n, i) => (
          <div key={n} style={{ display: 'contents' }}>
            {i > 0 && <div className="reg-step-line"></div>}
            <div className={`reg-step${step === n ? ' active' : step > n ? ' done' : ''}`}>
              <span className="reg-step-num">{n}</span>
              <span className="reg-step-label">{t(`acc.step${n}`)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ══ 1단계 약관동의 ══ */}
      {step === 1 && (
        <div className="form-box">
          <p className="reg-agree-desc">{t('acc.agreeDesc')}</p>
          <div className="reg-agree-all">
            <label className="form-check-item reg-agree-all-check">
              <input type="checkbox" checked={agree.terms && agree.privacy && agree.marketing}
                onChange={(e) => setAgree({ terms: e.target.checked, privacy: e.target.checked, marketing: e.target.checked })} />
              <span className="reg-agree-all-label">{t('acc.agreeAll')}</span>
            </label>
            <p className="reg-agree-all-desc">{t('acc.agreeAllDesc')}</p>
          </div>
          <div className="reg-agree-list">
            <div className="reg-agree-item">
              <label className="form-check-item">
                <input type="checkbox" checked={agree.terms} onChange={(e) => setAgree((a) => ({ ...a, terms: e.target.checked }))} />
                {' '}{t('footer.terms')} <span className="form-required">*</span>
              </label>
              <NoticeLink code="serviceterms" className="action-btn action-cancel btn-sm">{t('acc.viewTerms')}</NoticeLink>
            </div>
            <div className="reg-agree-item">
              <label className="form-check-item">
                <input type="checkbox" checked={agree.privacy} onChange={(e) => setAgree((a) => ({ ...a, privacy: e.target.checked }))} />
                {' '}{t('footer.privacy')} <span className="form-required">*</span>
              </label>
              <NoticeLink code="persnalinfo" className="action-btn action-cancel btn-sm">{t('acc.viewTerms')}</NoticeLink>
            </div>
            <div className="reg-agree-item">
              <label className="form-check-item">
                <input type="checkbox" checked={agree.marketing}
                  onChange={(e) => { setAgree((a) => ({ ...a, marketing: e.target.checked })); setRecvEmail(e.target.checked); }} />
                {' '}{t('acc.agreeMarketing')} <span className="reg-optional">({t('acc.optional')})</span>
              </label>
            </div>
          </div>
          <FieldMsg error={agreeError} />
          <div className="form-btns">
            <SmartLink className="action-btn action-cancel" href="/">{t('acc.toHome')}</SmartLink>
            {/* 잠그지 않는다 — 눌러서 무엇이 빠졌는지 알 수 있어야 한다 */}
            <button className="action-btn action-submit" onClick={nextFromAgree}>
              {t('acc.agreeAndJoin')}
            </button>
          </div>
        </div>
      )}

      {/* ══ 2단계 정보입력 ══ */}
      {step === 2 && (
        <div className="form-box">
          {(social.kakao || social.naver || social.google || social.apple) && (
            <div className="reg-sns-section">
              <p className="reg-sns-title">{t('acc.snsJoin')}</p>
              <SocialButtons social={social} mode="join" />
              <div className="form-divider mt16">{t('acc.orDirect')}</div>
            </div>
          )}
          <div className="form-wrap">
            <div className="form-field">
              <Label required>{t('acc.uid')}</Label>
              <div className="form-input-group">
                {/* 규칙에 없는 글자는 애초에 들어가지 않게 한다 — 한글을 IME 로 넣고 통과라 믿는 일을 막는다 */}
                <input type="text" className="form-input" value={f.uid} placeholder={t('acc.uidPh')}
                  maxLength={16} autoComplete="username"
                  onChange={(e) => { set({ uid: drop.filter('uid', e.target.value, /[^a-zA-Z0-9]/g, 'acc.onlyAlnum') }); setCheckedUid(null); }} />
                <button type="button" className="action-btn action-cancel btn-sm form-btn-nowrap"
                  disabled={busy} onClick={() => void checkUid()}>{t('acc.checkUid')}</button>
              </div>
              {/* 걸러 낸 글자 안내가 먼저 — 방금 친 것에 대한 답이라 통과 문구보다 급하다 */}
              <FieldMsg error={errors.uid} hint={drop.hint('uid') ?? (checkedUid === f.uid.trim() && f.uid ? t('acc.uidOk') : null)} />
            </div>

            <div className="form-field">
              <Label required>{t('acc.password')}</Label>
              <PwInput value={f.password} onChange={(v) => set({ password: v })} placeholder={t('acc.pwPh')} autoComplete="new-password" />
              <FieldMsg error={errors.password} />
            </div>
            <div className="form-field">
              <Label required>{t('acc.passwordConfirm')}</Label>
              <PwInput value={f.passwordConfirm} onChange={(v) => set({ passwordConfirm: v })} placeholder={t('acc.pwConfirmPh')} autoComplete="new-password" />
              <FieldMsg error={errors.passwordConfirm} />
            </div>
            <div className="form-field">
              <Label required>{t('acc.name')}</Label>
              <input type="text" className="form-input" value={f.name} placeholder={t('acc.namePh')}
                onChange={(e) => set({ name: e.target.value })} />
              <FieldMsg error={errors.name} />
            </div>

            {/* 인증 수단 — 둘 다 켜진 마켓에서만 고르게 한다(하나면 고를 것이 없다) */}
            {authEmail && authPhone && (
              <div className="form-field">
                <Label required>{t('acc.authType')}</Label>
                <div className="form-radio-group">
                  <label className="form-radio-item">
                    <input type="radio" checked={authType === 'email'} onChange={() => { setAuthType('email'); setCodeSent(false); }} />
                    {' '}{t('acc.authEmail')}
                  </label>
                  <label className="form-radio-item">
                    <input type="radio" checked={authType === 'phone'} onChange={() => { setAuthType('phone'); setCodeSent(false); }} />
                    {' '}{t('acc.authPhone')}
                  </label>
                </div>
              </div>
            )}

            {/* 이메일 — 이메일 인증이면 발송 버튼이 여기 붙고, 인증칸도 **바로 아래**에 나온다 */}
            <div className="form-field">
              <Label required>{t('acc.email')}</Label>
              {needsCode && authType === 'email' && <p className="form-hint accent">{t('acc.emailAuthHint')}</p>}
              <div className="form-input-group">
                <input type="email" className="form-input" value={f.email} placeholder={t('acc.emailPh')}
                  onChange={(e) => { set({ email: e.target.value }); setCodeSent(false); setCodeNotice(null); }} />
                {needsCode && authType === 'email' && (
                  <SendCodeButton onSend={() => void sendCode()} sending={busy} />
                )}
              </div>
              <FieldMsg error={errors.email} />
              {/* 발송 안내 — 이메일 인증일 때 이 칸 아래(2026-09-02) */}
              {authType === 'email' && codeNotice && (
                <p className="form-hint" style={{ color: codeNotice.error ? '#dc2626' : 'var(--accent)' }}>{codeNotice.text}</p>
              )}
            </div>
            {needsCode && authType === 'email' && codeSent && (
              <AuthCodeField label={t('acc.code')} code={code} onChange={setCode}
                seconds={left} onSend={() => void sendCode()} sending={busy} error={errors.authCode} />
            )}

            {/* 연락처 — 알림톡 인증이면 발송 버튼이 여기 붙고, 인증칸도 바로 아래에 나온다 */}
            <div className="form-field">
              <Label required>{t('acc.mobile')}</Label>
              {needsCode && authType === 'phone' && <p className="form-hint accent">{t('acc.phoneAuthHint')}</p>}
              <div className="form-input-group">
                <input type="tel" className="form-input" value={f.mobile} placeholder={t('acc.mobilePh')}
                  onChange={(e) => { set({ mobile: drop.filter('mobile', e.target.value, /\D/g, 'acc.onlyDigits') }); if (authType === 'phone') { setCodeSent(false); setCodeNotice(null); } }} />
                {needsCode && authType === 'phone' && (
                  <SendCodeButton onSend={() => void sendCode()} sending={busy} />
                )}
              </div>
              <FieldMsg error={errors.mobile} hint={drop.hint('mobile')} />
              {/* 발송 안내 — 알림톡 인증일 때 이 칸 아래(2026-09-02) */}
              {authType === 'phone' && codeNotice && (
                <p className="form-hint" style={{ color: codeNotice.error ? '#dc2626' : 'var(--accent)' }}>{codeNotice.text}</p>
              )}
            </div>
            {needsCode && authType === 'phone' && codeSent && (
              <AuthCodeField label={t('acc.code')} code={code} onChange={setCode}
                seconds={left} onSend={() => void sendCode()} sending={busy} error={errors.authCode} />
            )}

            <div className="form-field">
              <Label>{t('acc.recvNotify')}</Label>
              <div className="form-radio-group">
                <label className="form-radio-item">
                  <input type="radio" checked={recvEmail} onChange={() => setRecvEmail(true)} /> {t('acc.yes')}
                </label>
                <label className="form-radio-item">
                  <input type="radio" checked={!recvEmail} onChange={() => setRecvEmail(false)} /> {t('acc.no')}
                </label>
              </div>
            </div>

            <div className="form-field">
              <Label required>{t('acc.birthday')}</Label>
              <input type="text" className="form-input form-input-sm" maxLength={8} inputMode="numeric"
                value={f.birthday} placeholder={t('acc.birthdayPh')}
                onChange={(e) => set({ birthday: drop.filter('birthday', e.target.value, /\D/g, 'acc.onlyDigits') })} />
              <FieldMsg error={errors.birthday} hint={drop.hint('birthday')} />
            </div>

            <ZipField zipcode={f.zipcode} address={f.address} addressDetail={f.addressDetail}
              autoAddress={autoAddress} onChange={(v) => set(v)} />
            <FieldMsg error={errors.zipcode ?? errors.address} />
          </div>

          {notice && <p className="form-hint" style={{ color: 'var(--accent)' }}>{notice}</p>}
          <div className="form-btns form-btns-sep">
            <button className="action-btn action-cancel" onClick={() => goStep(1)}>{t('acc.prev')}</button>
            <button className="action-btn action-submit" disabled={busy} onClick={() => void submit()}>
              {busy && <Working inline />}{t('acc.join')}
            </button>
          </div>
        </div>
      )}

      {/* ══ 3단계 완료 ══ */}
      {step === 3 && (
        <div className="form-box reg-done">
          <div className="reg-done-icon"><Icon name="circle-check" size={48} /></div>
          <h2 className="reg-done-title">{t('acc.joinDone')}</h2>
          <p className="reg-done-desc">
            {t('acc.joinDoneDesc')}
            {joinPoint > 0 && <><br />{t('acc.joinPoint', { n: joinPoint.toLocaleString() })}</>}
          </p>
          <div className="form-btns form-btns-center">
            <SmartLink href="/" className="action-btn action-cancel">{t('acc.toHome')}</SmartLink>
            <SmartLink href="/login" className="action-btn action-submit">{t('acc.login')}</SmartLink>
          </div>
        </div>
      )}
    </>
  );
}
